import React from 'react';
import { C } from '../styles';

interface Props {
  catalog: any[];
  loading: boolean;
  isEarning: boolean;
  selectedId: string | number | null;
  isEditing: boolean;
  editTypeId: string | number | null;
  onSelect: (id: string | number) => void;
}

function taxSummary(item: any): string {
  const flags: string[] = [];
  if (item.federal_taxable) flags.push('Federal');
  if (item.cpp_contributable) flags.push('CPP');
  if (item.ei_insurable) flags.push('EI');
  if (item.vacationable) flags.push('Vacation');
  return flags.length ? flags.join(', ') : 'Non-taxable';
}

function formatRate(item: any, isEarning: boolean): string {
  const rate = isEarning ? item.default_rate : item.default_amount;
  const unit = item.unit_label || '';
  if (rate == null || rate === '') return 'Not set' + (unit ? ' ' + unit : '');
  const num = Number(rate);
  const formatted = num.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return formatted + (unit ? ' ' + unit : '');
}

export default function CatalogList({ catalog, loading, isEarning, selectedId, isEditing, editTypeId, onSelect }: Props) {
  if (loading) {
    return <div style={{ color: (C as any).muted, fontSize: 13, padding: '20px 0' }}>Loading catalog...</div>;
  }

  if (catalog.length === 0) {
    return (
      <div style={{
        background: '#FCFCFD', border: '1px solid ' + C.line,
        borderRadius: 8, padding: '32px 20px', textAlign: 'center',
      }}>
        <div style={{ fontSize: 13, color: (C as any).muted, marginBottom: 4 }}>
          No {isEarning ? 'pay types' : 'deduction types'} in your catalog yet
        </div>
        <div style={{ fontSize: 11.5, color: (C as any).faint }}>Add some in Payroll settings first</div>
      </div>
    );
  }

  return (
    <div style={{ background: '#fff', border: '1px solid ' + C.line, borderRadius: 8, overflow: 'hidden' }}>
      {catalog.map((item: any, idx: number) => {
        const isSelected = selectedId === item.id;
        const lastIdx = catalog.length - 1;
        const disabledThisRow = isEditing && item.id !== editTypeId;
        return (
          <div
            key={item.id}
            onClick={() => { if (!isEditing || item.id === editTypeId) onSelect(item.id); }}
            style={{
              display: 'flex', alignItems: 'center', gap: 14,
              padding: '13px 16px',
              borderBottom: idx < lastIdx ? '1px solid ' + (C as any).lineSoft : 'none',
              cursor: disabledThisRow ? 'not-allowed' : 'pointer',
              background: isSelected ? C.tealSoft : '#fff',
              transition: 'background 0.1s',
              opacity: disabledThisRow ? 0.4 : 1,
            }}
          >
            <div style={{
              width: 16, height: 16, borderRadius: '50%',
              border: '2px solid ' + (isSelected ? C.teal : C.line),
              flex: '0 0 16px',
              display: 'grid', placeItems: 'center',
              background: '#fff',
            }}>
              {isSelected && (
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: C.teal }} />
              )}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: C.ink, marginBottom: 2 }}>
                {item.name}
              </div>
              <div style={{
                fontSize: 11.5, color: (C as any).muted, lineHeight: 1.5,
                display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap',
              }}>
                {isEarning && <span>{taxSummary(item)}</span>}
                {!isEarning && item.is_pre_tax && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', fontSize: 10.5, fontWeight: 600, color: C.tealInk, background: C.tealSoft, padding: '2px 7px', borderRadius: 4 }}>Pre-tax</span>
                )}
                {!isEarning && !item.is_pre_tax && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', fontSize: 10.5, fontWeight: 600, color: (C as any).muted, background: (C as any).surface, padding: '2px 7px', borderRadius: 4 }}>Post-tax</span>
                )}
                {!isEarning && item.employer_matched && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', fontSize: 10.5, fontWeight: 600, color: (C as any).green, background: (C as any).greenSoft, padding: '2px 7px', borderRadius: 4 }}>Employer match</span>
                )}
              </div>
            </div>
            <div style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontVariantNumeric: 'tabular-nums',
              fontSize: 13, color: C.ink, fontWeight: 500,
              textAlign: 'right', flex: '0 0 auto',
            }}>
              {formatRate(item, isEarning)}
            </div>
          </div>
        );
      })}
    </div>
  );
}